import React, { useEffect, useState } from "react";
import {
  updateInterviewScheduleAction,
  creteNotificationAction,
} from "../../redux/action";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  TextField,
  Button,
  FormControl,
  MenuItem,
  Typography,
  Grid,
  CircularProgress,
  Select,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { userSelector } from "../../../user-redux/selector";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { myCandidatesSelector } from "../../redux/selector";

const Step7 = ({ onFinalizeRound }) => {
  const dispatch = useDispatch();

  const { candidateDetails, jobDetails, selectedSubmission, submissionStatus } =
    useSelector(myCandidatesSelector);

  const { user } = useSelector(userSelector);

  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSuccessToast, setOpenSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [contactError, setContactError] = useState("");

  const timezones = [
    { value: "IST", label: "IST" },
    { value: "UTC", label: "UTC" },
    { value: "GST", label: "GST" },
    { value: "GMT", label: "GMT" },
    { value: "EST", label: "EST" },
    { value: "CST", label: "CST" },
    { value: "PST", label: "PST" },
    { value: "IST", label: "IST" },
    { value: "BST", label: "BST" },
    { value: "AEST", label: "AEST" },
    { value: "JST", label: "JST" },
  ];

  useEffect(() => {
    setFormData({
      candidate_name: selectedSubmission?.candidate_name,
      position: selectedSubmission.jobtitle,
      status: "",
      candidate_email: candidateDetails?.email,
      phone: candidateDetails?.phone,
      benchsales_email: user?.username,
      candidateid: candidateDetails.candidate_id,
      clientname: selectedSubmission?.company_name,
      interview_date: "",
      interview_time: "",
      jobid: selectedSubmission?.job_id,
      reason: "",
      userid: selectedSubmission?.user_id,
      timeZone: "",
      role: selectedSubmission?.jobtitle,
      interview_mode: "Offline",
      benchsales_name: user?.full_name,
    });
  }, [candidateDetails, selectedSubmission]);

  // Helper for field labels
  const fieldLabels = {
    candidate_name: "Candidate Name",
    position: "Position",
    interview_date: "Date Of Interview",
    timeZone: "Time Zone",
    interview_mode: "Interview Mode",
    candidate_email: "Candidate Email",
    clientname: "Client Name",
    jobid: "Job ID",
    phone: "Contact Number (XXX)-XXX-XXXX",
  };

  // Update requiredFields to ensure candidate_name and position are required
  const requiredFields = [
    "candidate_name",
    "position",
    "clientname",
    "interview_date",
    "timeZone",
    "interview_mode",
    "jobid"
  ];

  // Validate a single field
  const validateField = (field, value) => {
    // Always require these fields
    if (["candidate_name", "position", "clientname"].includes(field)) {
      if (!value || value === "") {
        return `Please enter ${fieldLabels[field]}`;
      }
    }
    // For other fields, skip validation if status is Selected/Rejected
    if (formData.status === "Selected" || formData.status === "Rejected") {
      return "";
    }
    if (
      [
        "interview_date",
        "timeZone",
        "interview_mode",
        "candidate_email",
        "jobid"
      ].includes(field)
    ) {
      if (!value || value === "") {
        return `Please enter ${fieldLabels[field]}`;
      }
      if (field === "interview_date" && !moment(value).isValid()) {
        return `Please enter ${fieldLabels[field]}`;
      }
      if (field === "candidate_email") {
        // Basic email format check
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return "Please enter a valid email address";
        }
      }
    }
    return "";
  };

  // onBlur handler for each field
  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setFormErrors((prev) => ({ ...prev, [field]: error }));
  };

  // Replace handleChange function:
  const handleChange = (field, value) => {
    let date = moment(new Date(formData?.interview_date)).format("YYYY-MM-DD");
    let time = moment(new Date(formData?.interview_date)).format("LT");
    var timeZone = formData?.timeZone;
    if (field === "phone") {
      // Remove all non-numeric characters first
      const numericValue = value.replace(/[^0-9]/g, "");

      // Format the phone number as (XXX)-XXX-XXXX
      if (numericValue.length <= 10) {
        let formattedNumber = "";
        if (numericValue.length > 0) {
          formattedNumber = "(" + numericValue.slice(0, 3);
          if (numericValue.length > 3) {
            formattedNumber += ")-" + numericValue.slice(3, 6);
            if (numericValue.length > 6) {
              formattedNumber += "-" + numericValue.slice(6, 10);
            }
          }
        }
        value = formattedNumber;

        if (numericValue.length === 10) {
          setContactError("");
        } else if (numericValue.length === 0) {
          setContactError("");
        } else {
          setContactError("Please enter a valid phone number: (XXX)-XXX-XXXX");
        }
      } else {
        setContactError("Phone number should not exceed 10 digits");
        return;
      }
    }

    // If status changes, clear interview fields if not needed
    if (field === "status") {
      let clearedFields = {};
      // Always clear interview fields when switching to a new round
      if (
        (value === "Third Round" && selectedSubmission?.pStatusName === "Interview Round-II") ||
        (value === "Second Round" && selectedSubmission?.pStatusName === "Interview Scheduled By Vendor")
      ) {
        clearedFields = {
          interview_date: "",
          timeZone: "",
          interview_mode: "",
        };
      } else if (
        !((value === "Second Round" && selectedSubmission?.pStatusName === "Interview Scheduled By Vendor") ||
          (value === "Third Round" && selectedSubmission?.pStatusName === "Interview Round-II"))
      ) {
        clearedFields = {
          interview_date: "",
          timeZone: "",
          interview_mode: "",
        };
      }
      setFormData((prevFormData) => ({
        ...prevFormData,
        [field]: value,
        ...clearedFields,
      }));
      return;
    }

    // Validate on change if touched
    if (touched[field]) {
      const error = validateField(field, value);
      setFormErrors((prev) => ({ ...prev, [field]: error }));
    }

    if (field === "timeZone") {
      timeZone = value;
      setFormData((prevFormData) => ({
        ...prevFormData,
        [field]: value,
        interview_time: `${time} ${timeZone || ""}`,
      }));
    } else if (field === "interview_date") {
      date = value;
      setFormData((prevFormData) => ({
        ...prevFormData,
        [field]: value,
        interview_date: value,
        interview_time: `${time} ${timeZone || ""}`,
      }));
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [field]: value,
      }));
    }
  };

  // Add this function to validate all required fields on submit
  const validateAllFields = () => {
    let errors = {};
    let hasError = false;
    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
        hasError = true;
      }
    });
    setFormErrors((prev) => ({ ...prev, ...errors }));
    setTouched((prev) => ({
      ...prev,
      ...requiredFields.reduce((acc, field) => ({ ...acc, [field]: true }), {}),
    }));
    return !hasError;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.status) {
      errors.status = "Status is required";
    }

    // Example validation: Check if interview_date is empty
    if (
      formData.status !== "Selected" &&
      formData.status !== "Rejected" &&
      !formData.interview_date
    ) {
      errors.interview_date = "Interview date is required1";
    }

    // Add more validation checks for other fields as needed

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSuccessToastClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSuccessToast(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateAllFields()) {
      setToastMessage("Please fill all required fields correctly.");
      setOpenSuccessToast(true);
      return;
    }
    setIsSubmitting(true);
    // Continue with form submission logic
    const formattedInterviewDateTime = moment(formData.interview_date).format(
      "YYYY-MM-DD"
    );

    let updatedData = {
      candidateid: selectedSubmission?.candidate_id,
      jobid: selectedSubmission?.job_id,
      userid: selectedSubmission?.user_id,
      status:
        formData.status === "Third Round"
          ? "12"
          : formData.status === "Selected"
            ? "9"
            : formData.status === "Rejected"
              ? "10"
              : "11",
      role: candidateDetails?.role,
    };

    dispatch(
      updateInterviewScheduleAction(
        {
          ...formData,
          interview_date: formattedInterviewDateTime,
        },
        updatedData
      )
    )
      .then(async () => {
        setToastMessage("Form submitted successfully!");
        setOpenSuccessToast(true);
        if (formData.status === "Selected" || formData.status === "Rejected") {
          let finalizedRound = 1;
          if (selectedSubmission?.pStatusName === "Interview Round-II") {
            finalizedRound = 2;
          } else if (selectedSubmission?.pStatusName === "Interview Round-III") {
            finalizedRound = 3;
          }
          const key = `submissionFinalizedRound_${selectedSubmission?.candidate_id}_${selectedSubmission?.job_id}`;
          localStorage.setItem(key, finalizedRound);
          if (typeof onFinalizeRound === 'function') {
            onFinalizeRound(finalizedRound);
          }
        }
        if (formData.status === "Third Round") {
          setToastMessage("Interview Scheduled for Third Round");
          var notifyData = {
            user_id: user?.id,
            notifiedto: candidateDetails?.candidate_id,
            notificationDescription: "Interview Sheduled For 3rd Round",
            notificationSubject: "InterviewSheduled",
            notificationType: "Candidate Notification",
            job_id: jobDetails?.id,
            candidate_name: candidateDetails?.candidate_name,
            job_title: jobDetails?.jobtitle,
          };
        } else if (formData.status === "Selected") {
          setToastMessage("Candidate Selected");
          var notifyData = {
            user_id: user?.id,
            notifiedto: candidateDetails?.candidate_id,
            notificationDescription: "You are selected for a Job",
            notificationSubject: "Selected",
            notificationType: "Candidate Notification",
            job_id: jobDetails?.id,
            candidate_name: candidateDetails?.candidate_name,
            job_title: jobDetails?.jobtitle,
          };
        } else if (formData.status === "Rejected") {
          setToastMessage("Candidate Rejected");
          var notifyData = {
            user_id: user?.id,
            notifiedto: candidateDetails?.candidate_id,
            notificationDescription: "Your are Rejected for this role",
            notificationSubject: "Rejected",
            notificationType: "Candidate Notification",
            job_id: jobDetails?.id,
            candidate_name: candidateDetails?.candidate_name,
            job_title: jobDetails?.jobtitle,
          };
        } else {
          setToastMessage("Interview Scheduled ");
          var notifyData = {
            user_id: user?.id,
            notifiedto: candidateDetails?.candidate_id,
            notificationDescription: "Interview Sheduled For your Job",
            notificationSubject: "InterviewSheduled",
            notificationType: "Candidate Notification",
            job_id: jobDetails?.id,
            candidate_name: candidateDetails?.candidate_name,
            job_title: jobDetails?.jobtitle,
          };
        }
        await dispatch(creteNotificationAction(notifyData));
      })
      .catch((error) => {
        setToastMessage("Something went wrong");
        setOpenSuccessToast(true);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
    setIsSubmitting(false);
  };

  return (
    <div
      style={{
        marginTop: "30px",
        padding: "10px",
      }}
    >
      <form onSubmit={handleSubmit}>
        <Grid
          container
          spacing={2}
          sx={{
            ".css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
              padding: "8px 6px",
            },
            ".css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input":
              { padding: "8px 6px" },
            ".css-nxo287-MuiInputBase-input-MuiOutlinedInput-input": {
              padding: "8px 6px",
            },
          }}
        >
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Candidate Name
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formData.candidate_name || ""}
                onChange={(e) => handleChange("candidate_name", e.target.value)}
                onBlur={() => handleBlur("candidate_name")}
                error={!!formErrors.candidate_name}
                helperText={formErrors.candidate_name}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Position
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formData.position || ""}
                onChange={(e) => handleChange("position", e.target.value)}
                onBlur={() => handleBlur("position")}
                error={!!formErrors.position}
                helperText={formErrors.position}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Status
              </Typography>
              <Select
                id="status"
                label=""
                variant="outlined"
                value={formData.status}
                // onChange={handleInputChange("status")}
                onChange={(e) => handleChange("status", e.target.value)}
                sx={inputStyles}
              >
                {/* <MenuItem value="">Select Status</MenuItem> */}
                {submissionStatus?.bench_status_id ===
                  8 && (
                    <MenuItem value="Second Round">Second Round</MenuItem>
                  )}
                {submissionStatus?.bench_status_id === 11 && (
                  <MenuItem value="Third Round">Third Round</MenuItem>
                )}
                <MenuItem value="Selected">Selected</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {/* Show interview schedule fields ONLY if status is exactly 'Second Round' or 'Third Round' and NOT after 3rd round result */}
          {(formData.status === "Second Round" && submissionStatus?.bench_status_id === 8) ||
            (formData.status === "Third Round" && submissionStatus?.bench_status_id === 11) ? (
            <>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Typography paragraph sx={labelStyles}>
                      Date Of Interview
                    </Typography>
                    <DateTimePicker
                      fullWidth
                      inputVariant="outlined"
                      value={formData.interview_date || null}
                      onChange={(value) => handleChange("interview_date", value)}
                      slotProps={{
                        textField: {
                          onBlur: () => handleBlur("interview_date"),
                          error: !!formErrors.interview_date,
                          helperText: formErrors.interview_date,
                        },
                      }}
                      minDateTime={moment()}
                      disablePast
                    />
                  </FormControl>
                </Grid>
              </LocalizationProvider>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Typography paragraph sx={labelStyles}>
                    Time Zone
                  </Typography>
                  <TextField
                    select
                    variant="outlined"
                    value={formData.timeZone || ""}
                    onChange={(e) => handleChange("timeZone", e.target.value)}
                    onBlur={() => handleBlur("timeZone")}
                    error={!!formErrors.timeZone}
                    helperText={formErrors.timeZone}
                  >
                    {timezones.map((timezone, index) => (
                      <MenuItem key={index} value={timezone.value}>
                        {timezone.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Typography paragraph sx={labelStyles}>
                    Interview Mode
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    value={formData.interview_mode || ""}
                    onChange={(e) => handleChange("interview_mode", e.target.value)}
                    onBlur={() => handleBlur("interview_mode")}
                    error={!!formErrors.interview_mode}
                    helperText={formErrors.interview_mode}
                  >
                    <MenuItem value="Online">Online</MenuItem>
                    <MenuItem value="Offline">Offline</MenuItem>
                  </TextField>
                </FormControl>
              </Grid>
            </>
          ) : null}

          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Candidate Email
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formData.candidate_email || ""}
                onChange={(e) =>
                  handleChange("candidate_email", e.target.value)
                }
                onBlur={() => handleBlur("candidate_email")}
                error={!!formErrors.candidate_email}
                helperText={formErrors.candidate_email}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Client Name
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formData.clientname || ""}
                onChange={(e) => handleChange("clientname", e.target.value)}
                onBlur={() => handleBlur("clientname")}
                error={!!formErrors.clientname}
                helperText={formErrors.clientname}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Job ID
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formData.jobid || ""}
                onChange={(e) => handleChange("jobid", e.target.value)}
                onBlur={() => handleBlur("jobid")}
                error={!!formErrors.jobid}
                helperText={formErrors.jobid}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography paragraph sx={labelStyles}>
                Contact Number (XXX)-XXX-XXXX
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formData.phone || ""}
                onChange={(e) => handleChange("phone", e.target.value)}
                onBlur={() => handleBlur("phone")}
                error={!!formErrors.phone || !!contactError}
                helperText={formErrors.phone || contactError}
                inputProps={{
                  inputMode: "numeric",
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ textAlign: "end" }}>
            <Button
              type="submit"
              variant="contained"
              disabled={selectedSubmission?.pStatusName === "Selected" || selectedSubmission?.pStatusName === "Rejected" || ![8, 11, 12].includes(selectedSubmission?.bench_status_id)}
              sx={{
                bgcolor: "#406882",
                textTransform: "capitalize",
                width: 120,
                "&:hover": { bgcolor: "#406882" },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Submit"
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
      <Snackbar
        open={openSuccessToast}
        autoHideDuration={6000}
        onClose={handleSuccessToastClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={handleSuccessToastClose}
          severity={
            toastMessage === "Something went wrong" ? "error" : "success"
          }
        >
          {/* Form submitted successfully! */}
          {toastMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default Step7;

const labelStyles = {
  color: "rgb(160, 171, 184)",
  marginBottom: "5px",
};

const inputStyles = {
  border: "1px solid 	rgb(224, 230, 247)",
  borderRadius: "4px",
  "&:hover": {
    border: "1px solid 	rgb(224, 230, 247)",
    borderRadius: "4px",
  },
};
